import { adminService } from "@/api/admin/admin.service";
import { extractUsersList, type TAdminUser } from "@/api/admin/admin.types";
import { Input } from "@/components/ui/Input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { cn } from "@/lib/utils";
import { AlertTriangle, ChevronDown, Loader2, Search, UserRound, X } from "lucide-react";
import { useEffect, useState } from "react";

type TUserPickerProps = {
  /** The currently selected user, or null when nothing is chosen. */
  value: TAdminUser | null;
  /** Called with the selected user, or null when the selection is cleared. */
  onChange: (user: TAdminUser | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

const fullName = (u: TAdminUser) => `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();

/**
 * Searchable user selector. Admins pick a user by name or email instead of
 * pasting a raw UUID; the resolved user (including its id) is handed back via
 * `onChange`. Results come from the admin users endpoint's `search` param.
 */
export function UserPicker({
  value,
  onChange,
  placeholder = "Search by name or email…",
  disabled,
  className,
}: TUserPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<TAdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounced server-side search, run only while the dropdown is open.
  useEffect(() => {
    if (!open) return;
    const term = search.trim();
    const ctrl = new AbortController();
    const run = async () => {
      setLoading(true);
      setError(null);
      const { data, error: err } = await adminService.getUsers(
        { search: term || undefined, page: 1, page_size: 10 },
        { signal: ctrl.signal }
      );
      if (ctrl.signal.aborted) return;
      if (err) setError("Failed to search users");
      else setResults(extractUsersList(data?.data));
      setLoading(false);
    };
    const handle = setTimeout(() => void run(), 250);
    return () => {
      clearTimeout(handle);
      ctrl.abort();
    };
  }, [search, open]);

  const handleSelect = (user: TAdminUser) => {
    onChange(user);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-left text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <UserRound className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          {value ? (
            <span className="flex-1 truncate">
              <span className="font-medium text-foreground">{fullName(value) || value.email}</span>
              {fullName(value) && (
                <span className="text-muted-foreground"> · {value.email}</span>
              )}
            </span>
          ) : (
            <span className="flex-1 truncate text-muted-foreground">{placeholder}</span>
          )}
          {value ? (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Clear selection"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
              className="rounded p-0.5 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </span>
          ) : (
            <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="relative border-b border-border">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Type a name or email…"
            className="h-10 border-0 pl-9 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching…
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 px-3 py-4 text-xs text-destructive">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          ) : results.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {search.trim() ? "No users match your search." : "Start typing to search users."}
            </p>
          ) : (
            <ul>
              {results.map((user) => {
                const name = fullName(user);
                const isSelected = value?.id === user.id;
                return (
                  <li key={user.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(user)}
                      className={cn(
                        "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-accent",
                        isSelected && "bg-accent"
                      )}
                    >
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate font-medium text-foreground">
                          {name || user.email}
                        </span>
                        {name && (
                          <span className="truncate text-xs text-muted-foreground">
                            {user.email}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
